import { Lock } from "lucide-react";
import { useId, useRef, useState, type FormEvent } from "react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { cn } from "@/shared/lib/utils";
import { Avatar, AiChip } from "./avatar";
import { BottomSheet } from "./bottom-sheet";
import {
  useAssistants,
  useCreateGroup,
  useOpenConversation,
  type CreateGroup,
} from "../api/use-directory";
import { assistantName } from "../lib/assistant-name";
import { lockedMessage } from "../lib/handler-lock";
import type { AiRule, Contact, Identity } from "../types";
import { ERROR_CLASS } from "./styles";

const FIELDSET_CLASS = "mb-4 min-w-0";

const LEGEND_CLASS =
  "mb-2 font-mono text-xs tracking-[0.16em] text-muted-foreground uppercase";

const EMPTY_CLASS = "text-xs text-muted-foreground";

const CHOICE_CLASS = "flex cursor-pointer items-center gap-3";

const CHOICE_CARD_CLASS = "mb-1.5 rounded-lg border border-border p-2";

const CHOICE_SELECTED_CLASS = "border-gold/60 bg-accent/10";

const CHECKBOX_CLASS = "order-3 ml-auto size-4 shrink-0 accent-gold";

const CHOICE_DETAIL_CLASS = "block truncate text-xs text-muted-foreground";

/** A modal inside the floating panel, with its own focus and Escape boundary. */
export function GroupDialog({
  identity,
  kinds,
  contacts,
  onClose,
  onCreated,
}: {
  identity: Identity;
  kinds: readonly ("DIRECT" | "GROUP")[];
  contacts: Contact[];
  onClose: () => void;
  onCreated: (chatId: string) => void;
}) {
  const formId = useId();
  const nameId = useId();
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState(kinds[0]);
  const direct = useOpenConversation(identity);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const assistants = useAssistants(identity);
  const [selectedAssistants, setSelectedAssistants] = useState<Record<
    string,
    AiRule
  > | null>(null);
  const lockedIds = new Set(
    (assistants.data?.assistants ?? [])
      .filter((assistant) => assistant.locked)
      .map((assistant) => assistant.memberId),
  );
  const chosen =
    selectedAssistants ??
    Object.fromEntries(
      (assistants.data?.assistants ?? [])
        .filter((assistant) => assistant.isDefault && !assistant.locked)
        .map((assistant) => [assistant.memberId, "ADDRESSED_ONLY" as const]),
    );
  const selected = Object.fromEntries(
    Object.entries(chosen).filter(([memberId]) => !lockedIds.has(memberId)),
  );
  const selectedCount = memberIds.length + Object.keys(selected).length;
  const group = useCreateGroup(identity);
  const previousRequest = useRef<CreateGroup | null>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (kind === "DIRECT") {
      if (memberIds.length === 1 && !direct.isPending)
        direct.mutate(memberIds[0], {
          onSuccess: ({ chatId }) => onCreated(chatId),
        });
      return;
    }
    if (
      group.isPending ||
      !title.trim() ||
      !selectedCount ||
      selectedCount > 99
    )
      return;
    const content = {
      title: title.trim(),
      memberIds: [...memberIds].sort(),
      assistants: Object.entries(selected)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([memberId, aiRule]) => ({ memberId, aiRule })),
    };
    const previous = previousRequest.current;
    const unchanged =
      previous &&
      previous.title === content.title &&
      JSON.stringify(previous.assistants) ===
        JSON.stringify(content.assistants) &&
      previous.memberIds.join(",") === content.memberIds.join(",");
    const body = {
      ...content,
      reqKey: unchanged ? previous.reqKey : crypto.randomUUID(),
    };
    previousRequest.current = body;
    group.mutate(body, { onSuccess: ({ chatId }) => onCreated(chatId) });
  };
  const disabled =
    kind === "DIRECT"
      ? memberIds.length !== 1 || direct.isPending
      : group.isPending ||
        !title.trim() ||
        !selectedCount ||
        selectedCount > 99;
  return (
    <BottomSheet
      title="New chat"
      onClose={onClose}
      footer={
        <>
          {(group.error || direct.error) && (
            <p className={cn(ERROR_CLASS, "mb-3")} role="alert">
              {group.error?.message || direct.error?.message}
            </p>
          )}
          <Button
            type="submit"
            form={formId}
            className="min-h-11 w-full cursor-pointer motion-reduce:transition-none"
            disabled={disabled}
          >
            {kind === "DIRECT"
              ? direct.isPending
                ? "Opening…"
                : "Start chat"
              : group.isPending
                ? "Creating…"
                : group.error
                  ? "Retry group creation"
                  : "Create group"}
          </Button>
        </>
      }
    >
      <form id={formId} className="text-sm" onSubmit={submit}>
        {kinds.length > 1 && (
          <div
            className="mb-4 grid grid-cols-2 rounded-lg border border-border p-1 font-mono text-xs tracking-[0.05em] uppercase"
            aria-label="Conversation type"
          >
            {kinds.map((value) => (
              <Button
                key={value}
                type="button"
                variant="ghost"
                aria-pressed={kind === value}
                className={cn(
                  "min-h-11 cursor-pointer rounded-md text-xs uppercase motion-reduce:transition-none",
                  kind === value
                    ? "bg-secondary text-gold hover:bg-secondary hover:text-gold dark:hover:bg-secondary"
                    : "text-muted-foreground",
                )}
                onClick={() => {
                  setKind(value);
                  setMemberIds([]);
                  group.reset();
                  direct.reset();
                }}
              >
                {value === "DIRECT" ? "Direct" : "Group"}
              </Button>
            ))}
          </div>
        )}
        {kind === "GROUP" && (
          <>
            <Label className="sr-only" htmlFor={nameId}>
              Group name
            </Label>
            <Input
              autoFocus
              id={nameId}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Group title"
              maxLength={160}
              required
              className="mb-4 h-11"
            />
          </>
        )}
        <fieldset className={FIELDSET_CLASS}>
          <legend className={LEGEND_CLASS}>People</legend>
          {contacts.map((contact) => {
            const checked = memberIds.includes(contact.memberId);
            return (
              <label
                className={cn(
                  CHOICE_CLASS,
                  CHOICE_CARD_CLASS,
                  checked && CHOICE_SELECTED_CLASS,
                )}
                key={contact.memberId}
              >
                <input
                  type="checkbox"
                  aria-label={contact.name}
                  className={CHECKBOX_CLASS}
                  checked={checked}
                  onChange={(event) =>
                    setMemberIds((ids) =>
                      event.target.checked
                        ? kind === "DIRECT"
                          ? [contact.memberId]
                          : [...ids, contact.memberId]
                        : ids.filter((id) => id !== contact.memberId),
                    )
                  }
                />
                <Avatar name={contact.name} size="sm" />
                <span className="min-w-0 flex-1">
                  {contact.name}
                  <small className={CHOICE_DETAIL_CLASS}>Contact</small>
                </span>
              </label>
            );
          })}
          {!contacts.length && (
            <p className={EMPTY_CLASS}>No eligible contacts are available.</p>
          )}
        </fieldset>
        {kind === "GROUP" && (
          <fieldset className={FIELDSET_CLASS}>
            <legend className={LEGEND_CLASS}>Assistants</legend>
            {assistants.isPending && (
              <p className={EMPTY_CLASS}>Loading assistants…</p>
            )}
            {assistants.error && (
              <p className={ERROR_CLASS} role="alert">
                {assistants.error.message}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="min-h-11 cursor-pointer motion-reduce:transition-none"
                  onClick={() => void assistants.refetch()}
                >
                  Retry
                </Button>
              </p>
            )}
            {assistants.data?.assistants.map((assistant) => (
              <div
                className={cn(
                  CHOICE_CARD_CLASS,
                  selected[assistant.memberId] && CHOICE_SELECTED_CLASS,
                  assistant.locked && "opacity-70",
                )}
                key={assistant.memberId}
              >
                <label
                  className={cn(
                    CHOICE_CLASS,
                    assistant.locked && "cursor-not-allowed",
                  )}
                >
                  <input
                    type="checkbox"
                    disabled={assistant.locked}
                    aria-label={`Include ${assistant.name}`}
                    className={CHECKBOX_CLASS}
                    checked={
                      !assistant.locked && Boolean(selected[assistant.memberId])
                    }
                    onChange={(event) => {
                      const next = { ...selected };
                      if (event.target.checked)
                        next[assistant.memberId] = "ADDRESSED_ONLY";
                      else delete next[assistant.memberId];
                      setSelectedAssistants(next);
                    }}
                  />
                  <Avatar name={assistant.name} size="sm" ai />
                  <span className="min-w-0 flex-1">
                    {assistant.name} <AiChip />
                    {assistant.locked ? (
                      <small
                        className={cn(
                          CHOICE_DETAIL_CLASS,
                          "flex items-center gap-1",
                        )}
                        title={lockedMessage(
                          assistant,
                          assistantName(undefined, [assistant]),
                        )}
                      >
                        <Lock className="size-3 shrink-0" aria-hidden />
                        <span className="truncate">
                          {lockedMessage(
                            assistant,
                            assistantName(undefined, [assistant]),
                          )}
                        </span>
                      </small>
                    ) : (
                      assistant.purpose && (
                        <small className={CHOICE_DETAIL_CLASS}>
                          {assistant.purpose}
                        </small>
                      )
                    )}
                  </span>
                </label>
                {selected[assistant.memberId] && !assistant.locked && (
                  <div
                    className="mt-2 grid grid-cols-2 gap-1"
                    aria-label={`${assistant.name} reply mode`}
                  >
                    {(
                      [
                        ["ADDRESSED_ONLY", "Only when @mentioned"],
                        ["ALL_MESSAGES", "Reads every message"],
                      ] as const
                    ).map(([value, label]) => {
                      const pressed = selected[assistant.memberId] === value;
                      return (
                        <Button
                          key={value}
                          type="button"
                          variant="ghost"
                          aria-label={`${assistant.name}: ${label}`}
                          aria-pressed={pressed}
                          className={cn(
                            "h-auto min-h-11 min-w-0 cursor-pointer rounded-md border px-2 py-1 text-xs whitespace-normal motion-reduce:transition-none",
                            pressed
                              ? "border-gold/60 text-gold hover:text-gold"
                              : "border-border text-muted-foreground",
                          )}
                          onClick={() =>
                            setSelectedAssistants({
                              ...selected,
                              [assistant.memberId]: value,
                            })
                          }
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </fieldset>
        )}
      </form>
    </BottomSheet>
  );
}
